import React from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Grid,
  SpaceBetween,
  Link,
  ExpandableSection,
  Table,
  ColumnLayout,
  Container,
} from "@amzn/awsui-components-react";
import { TEMPLATE_APP, TEMPLATE_APP_TEAM } from "../Constants";
import CustomSelectMultiAlias from "./CustomSelectMultiAlias";
import CustomMultiHybridInputAndSelect from "./CustomMultiHybridInputAndSelect";
import CustomSelect from "./CustomSelect";
import CustomInput from "./CustomInput";
import CustomSelectAlias from "./CustomSelectAlias";

const TeamDetailsTile = ({
  isEditable,
  getValue,
  updateValue,
  getSelectFieldValues,
  getMultiInputFieldValues,
  shouldShowMandatory,
  mandatoryFieldListener,
  accountingValidation,
  postProcessTeamEmails,
  suggestedLogins,
  getSuggestedLoginsCaller,
  aliasesLoading,
  directReports,
  getDirectReportsCaller,
  directReportsLoading
}) => {
  const currentStage = useSelector((globalState) => globalState.auth.currentStage);

  const getGrcControlUrl = (grcControlId) => {
    if (!grcControlId) return null;
    const baseUrl = currentStage === 'prod' 
      ? 'https://grc.a2z.com' 
      : 'https://gamma.grc.a2z.com';
    return `${baseUrl}/docs/Control/${grcControlId}/publication`;
  };
  const renderMandatoryIndicator = () => (
    <Box color="text-status-error" fontSize="heading-l" fontWeight="bold">
      *
    </Box>
  );

  const renderProcessOwnerSection = () => (
    <ExpandableSection
      defaultExpanded={true}
      headerText="Process Owner (s)"
      variant="borderless"
    >
      {isEditable(TEMPLATE_APP.PROCESS_OWNER) ? (
        <Grid gridDefinition={[{ colspan: 11 }, { colspan: 1 }]}>
          <CustomSelectMultiAlias 
            field={TEMPLATE_APP.PROCESS_OWNER} 
            getValues={getMultiInputFieldValues} 
            updateValue={updateValue} 
            options={suggestedLogins} 
            getOptions={getSuggestedLoginsCaller} 
            aliasesLoading={aliasesLoading} 
          />
          {shouldShowMandatory() && renderMandatoryIndicator()}
        </Grid>
      ) : (
        <SpaceBetween size="xs">
          {getMultiInputFieldValues(TEMPLATE_APP.PROCESS_OWNER)?.map((item) => (
            <Box key={item.trim()} variant="p">
              <Link href={`https://phonetool.amazon.com/users/${item.trim()}`} target="_blank">
                {item.trim()}
              </Link>
            </Box>
          ))}
        </SpaceBetween>
      )}
    </ExpandableSection>
  );

  const renderAccountingVPSection = () => (
    <ExpandableSection
      defaultExpanded={true}
      headerText="Accounting VP"
      variant="borderless"
    >
      {isEditable(TEMPLATE_APP.VP) ? (
        <Grid gridDefinition={[{ colspan: 11 }, { colspan: 1 }]}>
          <CustomSelectMultiAlias 
            field={TEMPLATE_APP.VP} 
            getValues={getMultiInputFieldValues} 
            updateValue={updateValue} 
            options={directReports} 
            getOptions={() => getDirectReportsCaller('shelleyr')} 
            aliasesLoading={directReportsLoading} 
          />
          {shouldShowMandatory() && renderMandatoryIndicator()}
        </Grid>
      ) : (
        <SpaceBetween size="xs">
          {getMultiInputFieldValues(TEMPLATE_APP.VP)?.map((item) => (
            <Box key={item} variant="p">
              <Link href={`https://phonetool.amazon.com/users/${item.trim()}`} target="_blank">
                {item.trim()}
              </Link>
            </Box>
          ))}
        </SpaceBetween>
      )}
    </ExpandableSection>
  );

  const renderAccountingTeamSection = () => (
    <ExpandableSection
      defaultExpanded={true}
      headerText="Accounting team"
      variant="borderless"
    >
      {isEditable(TEMPLATE_APP.ACCOUNTING_TEAM) ? (
        <CustomMultiHybridInputAndSelect 
          field={TEMPLATE_APP.ACCOUNTING_TEAM}
          inputKey={TEMPLATE_APP.TEAM_SUB.EMAIL} 
          selectKey={TEMPLATE_APP.TEAM_SUB.TEAM} 
          selectHint="Choose team"
          placeholder="email" 
          getValues={getValue} 
          postProcess={postProcessTeamEmails} 
          validator={accountingValidation} 
          mandatoryFieldListener={mandatoryFieldListener}
          updateValue={updateValue} 
          getOptions={getSelectFieldValues} 
          info='For emails, type the alias only (without @amazon.com)' 
        />
      ) : (
        <>
          {getValue(TEMPLATE_APP.ACCOUNTING_TEAM) ? (
            <Table
              columnDefinitions={[
                {
                  id: "variable",
                  header: "Team",
                  cell: item => item.team || "-",
                  isRowHeader: true
                },
                {
                  id: "alt",
                  header: "Alias",
                  cell: item => item.email || "-",
                  sortingField: "alt"
                }
              ]}
              items={getValue(TEMPLATE_APP.ACCOUNTING_TEAM)}
              loadingText="Loading team details"
              sortingDisabled
              empty={
                <Box margin={{ vertical: "xs" }} textAlign="center" color="inherit">
                  <SpaceBetween size="m">
                    <Box variant="strong">No resources</Box>
                  </SpaceBetween>
                </Box>
              }
              variant="embedded"
            />
          ) : (
            <Box textAlign="center" color="text-body-secondary">
          
            </Box>
          )}
        </>
      )}
    </ExpandableSection>
  );

  const renderDeveloperTeamSection = () => (
    <SpaceBetween size="xs">
      <Box variant="h4">Developer Team</Box>
      {isEditable(TEMPLATE_APP.DEVELOPER_TEAM) ? (
        <CustomSelect 
          field={TEMPLATE_APP.DEVELOPER_TEAM} 
          subKey={TEMPLATE_APP.TEAM_SUB.TEAM} 
          getValue={getValue} 
          updateValue={updateValue} 
          getOptions={getSelectFieldValues} 
        />
      ) : (
        <Box variant="p">
          {getValue(TEMPLATE_APP.DEVELOPER_TEAM, TEMPLATE_APP.TEAM_SUB.TEAM) || 
           <Box color="text-body-secondary">Not specified</Box>
          }
        </Box>
      )}
    </SpaceBetween>
  );

  const renderDeveloperTeamEmailSection = () => (
    <SpaceBetween size="xs">
      <Box variant="h4">Developer Team Email</Box>
      {isEditable(TEMPLATE_APP.DEVELOPER_TEAM) ? (
        <CustomInput 
          field={TEMPLATE_APP.DEVELOPER_TEAM} 
          subKey={TEMPLATE_APP.TEAM_SUB.EMAIL}
          placeholder="email" 
          updateValue={updateValue} 
          getValue={getValue} 
          postProcess={postProcessTeamEmails}
          info='For emails, type the alias only (without @amazon.com)'
        />
      ) : (
        <Box variant="p">
          {getValue(TEMPLATE_APP.DEVELOPER_TEAM, TEMPLATE_APP.TEAM_SUB.EMAIL) || 
           <Box color="text-body-secondary">Not specified</Box>
          }
        </Box>
      )}
    </SpaceBetween>
  );

  const renderGRCControlOwnerSection = () => (
    <ExpandableSection
      defaultExpanded={true}
      headerText="GRC Control Owner"
      variant="borderless"
    >
      {isEditable(TEMPLATE_APP.GRC_CONTROL_OWNER) ? (
        <Grid gridDefinition={[{ colspan: 11 }, { colspan: 1 }]}>
          <CustomSelectAlias 
            field={TEMPLATE_APP.GRC_CONTROL_OWNER} 
            getValue={getValue} 
            updateValue={updateValue} 
            options={suggestedLogins} 
            getOptions={getSuggestedLoginsCaller} 
            aliasesLoading={aliasesLoading} 
          />
          {shouldShowMandatory() && renderMandatoryIndicator()}
        </Grid>
      ) : (
        <SpaceBetween size="xs">
          {getMultiInputFieldValues(TEMPLATE_APP.GRC_CONTROL_OWNER)?.map((item) => (
            <Box key={item.trim()} variant="p">
              <Link href={`https://phonetool.amazon.com/users/${item.trim()}`} target="_blank">
                {item.trim()}
              </Link>
            </Box>
          ))}
        </SpaceBetween>
      )}
    </ExpandableSection>
  );

  const renderGRCControlIdSection = () => {
    const grcControlId = getValue(TEMPLATE_APP.GRC_CONTROL_ID);
    const grcUrl = getGrcControlUrl(grcControlId);
    
    return (
      <SpaceBetween size="xs">
        <Box variant="h4">GRC Control Id</Box>
        <Box variant="p">
          {grcControlId ? (
            <Link href={grcUrl} target="_blank" external>
              {grcControlId}
            </Link>
          ) : (
            <Box color="text-body-secondary">Not specified</Box>
          )}
        </Box>
      </SpaceBetween>
    );
  };

  const renderCreatedBySection = () => (
    <SpaceBetween size="xs">
      <Box variant="h4">Created by</Box>
      <Box variant="p">
        <Link href={`https://phonetool.amazon.com/users/${getValue(TEMPLATE_APP.CREATED_BY)}`} target="_blank">
          {getValue(TEMPLATE_APP.CREATED_BY) || 
           <Box color="text-body-secondary">Not specified</Box>
          }
        </Link>
      </Box>
    </SpaceBetween>
  );

  return (
    <Box padding={{ left: "l" }}>
      <SpaceBetween size="l">
        <ColumnLayout columns={2}>
        {renderProcessOwnerSection()}

        {renderAccountingVPSection()}

        {renderAccountingTeamSection()}

        <Box>
          {/* Empty box for spacing */}
        </Box>

        {renderDeveloperTeamSection()}

        {renderDeveloperTeamEmailSection()}

        {renderGRCControlOwnerSection()}

        {renderGRCControlIdSection()}

        {renderCreatedBySection()}
        </ColumnLayout>
      </SpaceBetween>
    </Box>
  );
};

export default TeamDetailsTile;
